import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, FileText, Code2, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useGetOnboardingQuery,
  useUpdateOnboardingMutation,
  useSimulateTestPaymentMutation,
} from '../../../store/api/onboardingApi';
import WizardCard from '../WizardCard';

const CURL_SNIPPET = `# Create a test payment
curl https://api.maxelpay.com/v1/payments \\
  -H "Authorization: Bearer sk_test_4Xf..." \\
  -d amount="50.00" \\
  -d currency="USD" \\
  -d asset="USDC.base"`;

/**
 * Screen 4. Both paths converge here for the aha moment: one test payment that
 * moves a live status pill from Pending to Succeeded. "Simulate payment" covers
 * merchants without a test wallet. On success we set first_test_payment and
 * advance onboarding_status to payment_done.
 */
const TestPayment = () => {
  const navigate = useNavigate();
  const { data: state, isLoading } = useGetOnboardingQuery();
  const [updateOnboarding] = useUpdateOnboardingMutation();
  const [simulateTestPayment] = useSimulateTestPaymentMutation();

  // ui: 'idle' | 'pending' | 'succeeded' | 'failed'
  const [ui, setUi] = useState('idle');
  const timer = useRef(null);

  useEffect(() => {
    if (state?.first_test_payment === 'succeeded') setUi('succeeded');
  }, [state]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const simulate = async () => {
    setUi('pending');
    try {
      await updateOnboarding({ first_test_payment: 'pending' }).unwrap();
      // Brief delay so the merchant sees the live Pending -> Succeeded change.
      timer.current = setTimeout(async () => {
        try {
          await simulateTestPayment().unwrap();
          await updateOnboarding({ onboarding_status: 'payment_done' }).unwrap();
          setUi('succeeded');
        } catch {
          setUi('failed');
        }
      }, 1600);
    } catch {
      setUi('failed');
    }
  };

  const copyCurl = async () => {
    try {
      await navigator.clipboard.writeText(CURL_SNIPPET);
      toast.success('Copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  if (isLoading) {
    return (
      <section className="wiz-card" aria-busy="true">
        <div className="wiz-skeleton" style={{ width: '60%', marginBottom: 24 }} />
        <div className="wiz-skeleton block" style={{ height: 96 }} />
      </section>
    );
  }

  const succeeded = ui === 'succeeded';

  return (
    <WizardCard
      title="Make your first test payment"
      subtitle="Try MaxelPay end to end in test mode. No real funds involved."
      primaryLabel={succeeded ? 'Continue' : 'Simulate payment'}
      onPrimary={succeeded ? () => navigate('/onboarding/done') : simulate}
      primaryLoading={ui === 'pending'}
      onBack={ui === 'pending' ? undefined : () => navigate('/onboarding/activate')}
      onSkip={ui === 'pending' || succeeded ? undefined : () => navigate('/onboarding/done')}
      secondary={
        <a
          href="https://docs.maxelpay.com/quickstart"
          target="_blank"
          rel="noreferrer"
        >
          Open quickstart →
        </a>
      }
    >
      <div className="wiz-tp-options">
        <button
          type="button"
          className="wiz-tp-option"
          onClick={() => navigate('/dashboard/invoices')}
        >
          <span className="rd-icon-tile" aria-hidden="true">
            <FileText size={20} />
          </span>
          <span className="wiz-tp-option-text">
            <span className="wiz-tp-option-title">Create a test invoice</span>
            <span className="wiz-tp-option-desc">
              Easiest path. Send yourself an invoice and pay it on the test network.
            </span>
          </span>
        </button>

        <button
          type="button"
          className="wiz-tp-option"
          onClick={() => navigate('/dashboard/developer?onboarding=1')}
        >
          <span className="rd-icon-tile" aria-hidden="true">
            <Code2 size={20} />
          </span>
          <span className="wiz-tp-option-text">
            <span className="wiz-tp-option-title">Use the API</span>
            <span className="wiz-tp-option-desc">
              For engineers. Grab your test key and run the curl quickstart.
            </span>
          </span>
        </button>
      </div>

      <div className="rd-code wiz-tp-code">
        <button type="button" className="rd-copy" onClick={copyCurl}>
          <Copy size={11} /> Copy
        </button>
        <pre>{CURL_SNIPPET}</pre>
      </div>

      <div className={`wiz-pill ${ui}`} role="status" aria-live="polite">
        {ui === 'idle' && (
          <span className="wiz-pill-text">
            Ready when you are. Run a test payment to see it land.
          </span>
        )}
        {ui === 'pending' && (
          <>
            <span className="wiz-pill-dot" aria-hidden="true" />
            <span className="wiz-pill-text">Waiting for payment...</span>
          </>
        )}
        {ui === 'succeeded' && (
          <>
            <CheckCircle2 size={20} aria-hidden="true" />
            <span className="wiz-pill-text">Payment received.</span>
          </>
        )}
        {ui === 'failed' && (
          <>
            <AlertCircle size={20} aria-hidden="true" />
            <span className="wiz-pill-text">
              That payment did not go through. Check your test token and try again.
            </span>
          </>
        )}
      </div>

      {ui === 'failed' && (
        <div className="wiz-retry-row">
          <button type="button" className="rd-btn rd-btn-primary" onClick={simulate}>
            Try again
          </button>
          <a
            href="https://docs.maxelpay.com/test-tokens"
            target="_blank"
            rel="noreferrer"
            className="wiz-link"
          >
            View supported test tokens
          </a>
        </div>
      )}
    </WizardCard>
  );
};

export default TestPayment;
